import {
  ContactCaptureCustomFieldSettings,
  ContactCaptureCustomFieldType,
  ContactCaptureFieldId,
  ContactCaptureCampaign,
  ContactCaptureSettings,
  ContactCaptureTextAlign,
  ContactCaptureValidationError,
} from '../types';

/** Every platform contact field the widget config may contain, in form order. */
export const CONTACT_CAPTURE_FIELD_IDS: ContactCaptureFieldId[] = [
  'firstName',
  'lastName',
  'email',
  'mobilePhone',
  'birthdate',
];

/** Names shared by settings.fields and the public submission contract. */
export const CONTACT_CAPTURE_FIELD_NAMES: Record<ContactCaptureFieldId, string> = {
  firstName: 'contact.standard.first_name',
  lastName: 'contact.standard.last_name',
  email: 'contact.standard.email',
  mobilePhone: 'contact.standard.phone_number',
  birthdate: 'contact.standard.birthdate',
};

export const CONTACT_CAPTURE_FONT_FAMILIES: ContactCaptureSettings['styles']['fontFamily'][] = [
  'Inter',
  'Georgia',
  'Arial',
];

export const CONTACT_CAPTURE_TEXT_ALIGNS: ContactCaptureTextAlign[] = ['left', 'center', 'right'];

export const CONTACT_CAPTURE_BRAND_NAME_SIZE = { min: 10, max: 32 };
export const CONTACT_CAPTURE_HEADER_TEXT_SIZE = { min: 14, max: 48 };

export const CONTACT_CAPTURE_FIELD_LABELS: Record<ContactCaptureFieldId, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  mobilePhone: 'Mobile phone',
  birthdate: 'Birthdate',
};

export const CONTACT_CAPTURE_CUSTOM_FIELD_TYPES: ContactCaptureCustomFieldType[] = [
  'string',
  'number',
  'date',
  'datetime',
  'dollars',
];

export type ContactCaptureCustomFieldSource = {
  metadataId: string | null;
  name: string;
  description: string;
  type: string;
};

export const createContactCaptureCustomField = (
  source: ContactCaptureCustomFieldSource,
): ContactCaptureCustomFieldSettings => {
  const label = source.description || source.name;
  return {
    name: source.name,
    metadataId: source.metadataId || '',
    fieldType: CONTACT_CAPTURE_CUSTOM_FIELD_TYPES.includes(source.type as ContactCaptureCustomFieldType)
      ? source.type as ContactCaptureCustomFieldType
      : 'string',
    label,
    placeholder: label,
    enabled: true,
    required: false,
  };
};

export const isCompleteContactCaptureCustomField = (
  field: ContactCaptureCustomFieldSettings,
) => Boolean(field.name.startsWith('contact.custom.') && field.label.trim());

export const DEFAULT_CONTACT_CAPTURE_SETTINGS: ContactCaptureSettings = {
  version: 1,
  behavior: {
    popupDelaySeconds: 0,
  },
  content: {
    brandName: '',
    headerText: '',
    showTitle: true,
    bodyText: '',
    buttonText: 'Submit',
    declineText: 'No thanks',
    successMessage: '',
  },
  styles: {
    primaryColor: '#6f4e37',
    buttonColor: '#b85c38',
    backgroundColor: '#fffdf8',
    textColor: '#32251e',
    overlayColor: '#120f0d',
    borderRadius: 16,
    fontFamily: 'Inter',
    brandNameAlign: 'left',
    brandNameSize: 12,
    brandNameBold: true,
    brandNameItalic: false,
    brandNameUppercase: true,
    headerTextAlign: 'left',
    headerTextSize: 21,
    headerTextBold: true,
    headerTextItalic: false,
    headerTextUppercase: false,
  },
  /** Every platform contact field, always offered to the builder and toggled with `enabled`. */
  fields: [
    { id: 'firstName', label: 'First name', placeholder: 'First name', enabled: true, required: true },
    { id: 'lastName', label: 'Last name', placeholder: 'Last name', enabled: false, required: false },
    { id: 'email', label: 'Email', placeholder: 'Email', enabled: true, required: true },
    { id: 'mobilePhone', label: 'Mobile phone', placeholder: 'Mobile phone', enabled: true, required: true },
    { id: 'birthdate', label: 'Birthdate', placeholder: 'Birthdate', enabled: false, required: false },
  ],
  customFields: [],
};

const cloneSettings = (settings: ContactCaptureSettings): ContactCaptureSettings =>
  JSON.parse(JSON.stringify(settings)) as ContactCaptureSettings;

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * Values reach here from the API and from the widget runtime, so every field falls back to its
 * default rather than trusting the input. Colors and fonts especially: they are written into CSS
 * custom properties on customer pages.
 */
const pick = {
  text: (value: unknown, fallback: string) => (typeof value === 'string' ? value : fallback),
  flag: (value: unknown, fallback: boolean) => (typeof value === 'boolean' ? value : fallback),
  clamped: (value: unknown, fallback: number, min: number, max: number) => (
    typeof value === 'number' && Number.isFinite(value)
      ? Math.max(min, Math.min(max, value))
      : fallback
  ),
  color: (value: unknown, fallback: string) => (
    typeof value === 'string' && HEX_COLOR.test(value) ? value : fallback
  ),
  oneOf: <T extends string>(value: unknown, allowed: readonly T[], fallback: T) => (
    allowed.includes(value as T) ? value as T : fallback
  ),
};

type ContactCaptureSettingsInput = {
  behavior?: Partial<ContactCaptureSettings['behavior']> | null;
  content?: Partial<ContactCaptureSettings['content']> | null;
  styles?: Partial<ContactCaptureSettings['styles']> | null;
  fields?: Array<Partial<ContactCaptureSettings['fields'][number] & ContactCaptureCustomFieldSettings>> | null;
  disabledFields?: Array<Partial<ContactCaptureSettings['fields'][number]>> | null;
  customFields?: Array<Partial<ContactCaptureCustomFieldSettings>> | null;
};

const normalizeCustomFields = (
  customFields?: Array<Partial<ContactCaptureCustomFieldSettings>> | null,
): ContactCaptureCustomFieldSettings[] => (customFields || [])
  .map(input => {
    const field = createContactCaptureCustomField({
      metadataId: input.metadataId || null,
      name: input.name || '',
      description: input.label || '',
      type: input.fieldType || 'string',
    });
    return {
      ...field,
      placeholder: pick.text(input.placeholder, field.placeholder),
      enabled: pick.flag(input.enabled, field.enabled),
      required: pick.flag(input.required, field.required),
    };
  })
  // Removing a custom field in the builder drops it; hidden ones saved earlier are dropped too.
  .filter(field => field.enabled && isCompleteContactCaptureCustomField(field))
  .filter((field, index, fields) => fields.findIndex(item => item.name === field.name) === index);

export const createDefaultContactCaptureSettings = (): ContactCaptureSettings =>
  cloneSettings(DEFAULT_CONTACT_CAPTURE_SETTINGS);

/** Accepts `#1A2B3C`, `1A2B3C` or `#ABC`; returns lowercase `#rrggbb`, or null when invalid. */
export const parseContactCaptureHexColor = (value: string): string | null => {
  const hex = value.trim().replace(/^#/, '');
  const full = /^[0-9a-f]{3}$/i.test(hex) ? hex.split('').map(char => char + char).join('') : hex;
  return HEX_COLOR.test(`#${full}`) ? `#${full}`.toLowerCase() : null;
};

export const normalizeContactCaptureSettings = (
  settings?: ContactCaptureSettingsInput | null,
): ContactCaptureSettings => {
  const { behavior, content, fields, styles } = createDefaultContactCaptureSettings();
  if (!settings) return createDefaultContactCaptureSettings();

  const inputContent = settings.content || {};
  const inputStyles = settings.styles || {};
  const inputFields = settings.fields || [];
  const usesApiNames = inputFields.some(field => typeof field.name === 'string');

  return {
    version: 1,
    behavior: {
      popupDelaySeconds: pick.clamped(
        settings.behavior?.popupDelaySeconds,
        behavior.popupDelaySeconds,
        0,
        300,
      ),
    },
    content: {
      brandName: pick.text(inputContent.brandName, content.brandName),
      headerText: pick.text(inputContent.headerText, content.headerText),
      showTitle: pick.flag(inputContent.showTitle, content.showTitle),
      bodyText: pick.text(inputContent.bodyText, content.bodyText),
      buttonText: pick.text(inputContent.buttonText, content.buttonText),
      declineText: pick.text(inputContent.declineText, content.declineText),
      successMessage: pick.text(inputContent.successMessage, content.successMessage),
    },
    styles: {
      primaryColor: pick.color(inputStyles.primaryColor, styles.primaryColor),
      buttonColor: pick.color(inputStyles.buttonColor, styles.buttonColor),
      backgroundColor: pick.color(inputStyles.backgroundColor, styles.backgroundColor),
      textColor: pick.color(inputStyles.textColor, styles.textColor),
      overlayColor: pick.color(inputStyles.overlayColor, styles.overlayColor),
      borderRadius: pick.clamped(inputStyles.borderRadius, styles.borderRadius, 0, 40),
      fontFamily: pick.oneOf(
        inputStyles.fontFamily,
        CONTACT_CAPTURE_FONT_FAMILIES,
        styles.fontFamily,
      ),
      brandNameAlign: pick.oneOf(
        inputStyles.brandNameAlign,
        CONTACT_CAPTURE_TEXT_ALIGNS,
        styles.brandNameAlign,
      ),
      brandNameSize: Math.round(pick.clamped(
        inputStyles.brandNameSize,
        styles.brandNameSize,
        CONTACT_CAPTURE_BRAND_NAME_SIZE.min,
        CONTACT_CAPTURE_BRAND_NAME_SIZE.max,
      )),
      brandNameBold: pick.flag(inputStyles.brandNameBold, styles.brandNameBold),
      brandNameItalic: pick.flag(inputStyles.brandNameItalic, styles.brandNameItalic),
      brandNameUppercase: pick.flag(inputStyles.brandNameUppercase, styles.brandNameUppercase),
      headerTextAlign: pick.oneOf(
        inputStyles.headerTextAlign,
        CONTACT_CAPTURE_TEXT_ALIGNS,
        styles.headerTextAlign,
      ),
      headerTextSize: Math.round(pick.clamped(
        inputStyles.headerTextSize,
        styles.headerTextSize,
        CONTACT_CAPTURE_HEADER_TEXT_SIZE.min,
        CONTACT_CAPTURE_HEADER_TEXT_SIZE.max,
      )),
      headerTextBold: pick.flag(inputStyles.headerTextBold, styles.headerTextBold),
      headerTextItalic: pick.flag(inputStyles.headerTextItalic, styles.headerTextItalic),
      headerTextUppercase: pick.flag(inputStyles.headerTextUppercase, styles.headerTextUppercase),
    },
    fields: fields.map(defaultField => {
      const declared = inputFields.find(item => usesApiNames
        ? item.name === CONTACT_CAPTURE_FIELD_NAMES[defaultField.id]
        : item.id === defaultField.id);
      const input = declared
        || settings.disabledFields?.find(item => item.id === defaultField.id) || {};
      const field = {
        id: defaultField.id,
        label: pick.text(input.label, defaultField.label),
        placeholder: pick.text(input.placeholder, defaultField.placeholder),
        enabled: usesApiNames ? Boolean(declared) : pick.flag(input.enabled, defaultField.enabled),
        required: pick.flag(input.required, defaultField.required),
      };
      // Mobile phone is the one field the platform always collects.
      return field.id === 'mobilePhone' ? { ...field, enabled: true, required: true } : field;
    }),
    customFields: normalizeCustomFields(usesApiNames
      ? inputFields.filter(field => field.name?.startsWith('contact.custom.')).map(field => ({ ...field, enabled: true }))
      : settings.customFields),
  };
};

/** Only collected fields belong in the backend's validation/submission allowlist. */
export const toContactCaptureApiSettings = (settings: ContactCaptureSettings): Record<string, unknown> => {
  const normalized = normalizeContactCaptureSettings(settings);
  return {
    ...normalized,
    fields: [
      ...normalized.fields.filter(field => field.enabled)
        .map(field => ({ ...field, name: CONTACT_CAPTURE_FIELD_NAMES[field.id] })),
      ...normalized.customFields,
    ],
    // Keep disabled rows editable without making the backend collect or require them.
    disabledFields: normalized.fields.filter(field => !field.enabled),
    // Custom fields are all collected, so they travel in `fields`; this key is kept for the API.
    customFields: [],
  };
};

/** Signups are enrolled server-side, which only accepts an active join campaign. */
export const isEligibleContactCaptureCampaign = (
  campaign: Pick<ContactCaptureCampaign, 'type' | 'status'> | null | undefined,
): campaign is ContactCaptureCampaign => campaign?.type === 'join' && campaign.status === 'active';

export const getContactCapturePublishErrors = (
  settings: ContactCaptureSettings,
  campaign: ContactCaptureCampaign | null,
): ContactCaptureValidationError[] => {
  const { content, customFields, fields } = settings;
  const mobilePhone = fields.find(field => field.id === 'mobilePhone');

  const rules: Array<[invalid: boolean, field: string, message: string]> = [
    [!content.headerText.trim(), 'Header text', 'Add a headline for the form.'],
    [!content.buttonText.trim(), 'Button text', 'Add a label for the submit button.'],
    [!content.successMessage.trim(), 'Success message', 'Add the confirmation message shown after submission.'],
    [!mobilePhone?.enabled || !mobilePhone.required, 'Mobile phone', 'Mobile phone must be enabled and required.'],
    [
      fields.some(field => field.enabled && !field.label.trim())
        || customFields.some(field => !isCompleteContactCaptureCustomField(field)),
      'Field labels',
      'Enter a label for every field on the form.',
    ],
    [!isEligibleContactCaptureCampaign(campaign), 'Campaign', 'Choose an active join campaign.'],
  ];

  return rules
    .filter(([invalid]) => invalid)
    .map(([, field, message]) => ({ field, message }));
};
